'use client';

import React, { forwardRef, useEffect, useRef, useState } from 'react';

const comparisonData = [
  {
    label: 'Pressure Washing',
    costPerSqFt: '$0.15–0.30',
    totalExample: '$320',
    barWidth: 8,
    color: 'var(--accent-blue)',
    verdict: 'Best ROI',
    verdictColor: 'var(--accent-blue)',
    note: 'Restores surface, prevents further damage, adds immediate curb appeal.',
  },
  {
    label: 'Exterior Repainting',
    costPerSqFt: '$1.50–3.50',
    totalExample: '$6,000',
    barWidth: 72,
    color: '#94a3b8',
    verdict: 'High Cost',
    verdictColor: '#94a3b8',
    note: 'Covers the problem. Does not remove biofilm or structural staining underneath.',
  },
  {
    label: 'Siding Replacement',
    costPerSqFt: '$4.00–9.00',
    totalExample: '$16,000',
    barWidth: 100,
    color: '#ef4444',
    verdict: 'Avoidable',
    verdictColor: '#ef4444',
    note: 'Required only when neglect progresses past the point pressure washing can reverse.',
  },
  {
    label: 'Property Value Decline',
    costPerSqFt: '$3.40/yr loss',
    totalExample: '$34,000',
    barWidth: 88,
    color: '#f97316',
    verdict: 'Hidden Cost',
    verdictColor: '#f97316',
    note: 'The cumulative cost of neglect over 10 years on an average $340K property.',
  },
];

// Quick-quote calculator
const surfaceTypes = [
  { id: 'driveway', label: 'Driveway / Concrete', rateMin: 0.15, rateMax: 0.30 },
  { id: 'deck', label: 'Wood Deck', rateMin: 0.25, rateMax: 0.45 },
  { id: 'siding', label: 'House Siding', rateMin: 0.15, rateMax: 0.30 },
  { id: 'windows', label: 'Windows', rateMin: 0.12, rateMax: 0.22 },
  { id: 'fleet', label: 'Fleet Vehicle', rateMin: 35, rateMax: 85 },
  { id: 'parking', label: 'Parking Structure', rateMin: 0.10, rateMax: 0.18 },
  { id: 'trashcans', label: 'Trash Cans', rateMin: 15, rateMax: 20 },
];

const CostComparison = forwardRef<HTMLElement>((_, ref) => {
  const [barsVisible, setBarsVisible] = useState(false);
  const barsRef = useRef<HTMLDivElement>(null);

  // Calculator state
  const [sqft, setSqft] = useState(1000);
  const [surfaceType, setSurfaceType] = useState('driveway');

  const selectedSurface = surfaceTypes.find((s) => s.id === surfaceType)!;
    const isPerUnit = surfaceType === 'fleet' || surfaceType === 'trashcans';
  const minCost = isPerUnit ? selectedSurface.rateMin : Math.round(sqft * selectedSurface.rateMin);
  const maxCost = isPerUnit ? selectedSurface.rateMax : Math.round(sqft * selectedSurface.rateMax);

  useEffect(() => {
    const el = barsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setBarsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="comparison"
      className="reveal-section"
      style={{
        background: 'var(--slate)',
        padding: 'clamp(4rem, 8vw, 7rem) clamp(2rem, 6vw, 6rem)',
      }}
    >
      {/* Section header */}
      <div className="mb-16">
        <div className="flex items-center gap-3 mb-4">
          <div style={{ width: '32px', height: '2px', background: 'var(--accent-blue)' }} />
          <span
            style={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--accent-blue)',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
          >
            § 04 — ROI Comparison
          </span>
        </div>
        <h2
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            color: 'var(--arctic-white)',
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            maxWidth: '600px',
          }}
        >
          The math on doing nothing{' '}
          <span style={{ color: 'var(--accent-blue)' }}>is brutal.</span>
        </h2>
        <p
          style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: '1rem',
            color: 'var(--mist-gray)',
            lineHeight: 1.65,
            maxWidth: '480px',
            marginTop: '1rem',
          }}
        >
          Cost-per-square-foot comparison: pressure washing versus repainting versus the compounding cost of property value decline.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
        {/* Comparison Chart */}
        <div
          ref={barsRef}
          style={{
            background: 'rgba(248,249,250,0.06)',
            border: '1px solid rgba(209,217,224,0.12)',
            borderRadius: '16px',
            padding: '2rem',
          }}
        >
          <div
            style={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'rgba(209,217,224,0.5)',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              marginBottom: '1.5rem',
            }}
          >
            Cost per 2,000 sq ft — Single-Family Home
          </div>

          <div className="space-y-8">
            {comparisonData.map((item, i) => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.9375rem',
                        color: 'var(--arctic-white)',
                      }}
                    >
                      {item.label}
                    </span>
                    <span
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: `1px solid ${item.verdictColor}40`,
                        color: item.verdictColor,
                        borderRadius: '4px',
                        padding: '0.125rem 0.5rem',
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                      }}
                    >
                      {item.verdict}
                    </span>
                  </div>
                  <div className="text-right">
                    <span
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.125rem',
                        color: item.color,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {item.totalExample}
                    </span>
                    <span
                      style={{
                        fontSize: '0.6875rem',
                        color: 'rgba(209,217,224,0.4)',
                        marginLeft: '0.25rem',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                      }}
                    >
                      {item.costPerSqFt}
                    </span>
                  </div>
                </div>

                {/* Bar */}
                <div className="comparison-bar" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <div
                    className="comparison-bar-fill"
                    style={{
                      background: item.color,
                      width: barsVisible ? `${item.barWidth}%` : '0%',
                      transitionDelay: `${i * 0.15}s`,
                    }}
                  />
                </div>

                <p
                  style={{
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '0.75rem',
                    color: 'rgba(209,217,224,0.45)',
                    marginTop: '0.5rem',
                    lineHeight: 1.5,
                  }}
                >
                  {item.note}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick-Quote Calculator */}
        <div
          style={{
            background: 'var(--white)',
            borderRadius: '16px',
            padding: '2rem',
            border: '1px solid rgba(209,217,224,0.3)',
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div
              style={{
                width: '36px',
                height: '36px',
                background: 'rgba(0,119,204,0.1)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-blue)" strokeWidth="2" strokeLinecap="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div>
              <h3
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  color: 'var(--slate)',
                  letterSpacing: '-0.01em',
                }}
              >
                Quick-Quote Calculator
              </h3>
              <p
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--mist-gray)',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                }}
              >
                No phone number required — instant ballpark estimate
              </p>
            </div>
          </div>

          {/* Surface Type */}
          <div className="mb-6">
            <label
              style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--slate)',
                marginBottom: '0.5rem',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                letterSpacing: '0.02em',
              }}
            >
              Surface Type
            </label>
            <select
              value={surfaceType}
              onChange={(e) => setSurfaceType(e.target.value)}
              className="blast-input"
              style={{ cursor: 'pointer' }}
            >
              {surfaceTypes.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Square Footage */}
          {!isPerUnit && (
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <label
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--slate)',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    letterSpacing: '0.02em',
                  }}
                >
                  Square Footage
                </label>
                <span
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'var(--accent-blue)',
                  }}
                >
                  {sqft.toLocaleString()} sq ft
                </span>
              </div>
              <input
                type="range"
                min={200}
                max={10000}
                step={100}
                value={sqft}
                onChange={(e) => setSqft(Number(e.target.value))}
                className="calc-range"
              />
              <div
                className="flex justify-between mt-1"
                style={{ fontSize: '0.625rem', color: 'var(--mist-gray)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                <span>200 sq ft</span>
                <span>10,000 sq ft</span>
              </div>
            </div>
          )}

          {/* Result */}
          <div
            style={{
              background: 'var(--arctic-white)',
              border: '2px solid var(--accent-blue)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--mist-gray)',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                marginBottom: '0.5rem',
              }}
            >
              Estimated Range
            </div>
            <div
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 700,
                fontSize: '2.25rem',
                color: 'var(--accent-blue)',
                letterSpacing: '-0.04em',
                lineHeight: 1,
              }}
            >
              ${minCost.toLocaleString()} – ${maxCost.toLocaleString()}
            </div>
            <div
              style={{
                fontSize: '0.75rem',
                color: 'var(--mist-gray)',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                marginTop: '0.375rem',
              }}
            >
              {isPerUnit ? (surfaceType === 'trashcans' ? 'per can' : 'per vehicle, depending on size') : `for ${sqft.toLocaleString()} sq ft of ${selectedSurface.label.toLowerCase()}`}
            </div>
          </div>

          <button
            className="btn-primary"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => {
              document.getElementById('capture')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Get Exact Quote
          </button>

          <p
            className="text-center mt-3"
            style={{
              fontSize: '0.6875rem',
              color: 'var(--mist-gray)',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
          >
            No commitment. No phone call required. Email only.
          </p>
        </div>
      </div>
    </section>
  );
});

CostComparison.displayName = 'CostComparison';
export default CostComparison;